export * from './LibraryStructs';
import type { DashboardStructs } from "./LibraryStructs";
import type { ToolbarIconInfo } from '../UI/Primitive/ToolbarIconButton';
import type { DateFormat } from '@Utils/Formats';
import type { BoardThemeColorOverride } from '@UI/Dashboard/BoardTheming';
// --------------------------------------------------------------------------------------------
export const enum DashboardType {
    /**
     * Default page support
     */
    Page = "page",

    /**
     * Reporting page 
     */
    Reporting = "reporting",

    /**
     * Wizard page
     */
    Wizard = "wizard"
}
// --------------------------------------------------------------------------------------------
export interface DashboardViewExt extends DashboardStructs.DashboardView {

    /**
     * The description of widgets
     */
    ViewWidgets: DashboardViewWidgets;

    /**
     * The dashboard settings defined by user.
     */
    Settings: DashboardConfiguration;
}
// --------------------------------------------------------------------------------------------
export interface DashboardConfiguration {
    /**
     * Type of the dashboard
     */
    Type: DashboardType;

    /**
     * Canvas background image. 
            This is stored in base64 format
     */
    BackgroundImage: string;

    /**
     * background image for mobile view in base64 format
     */
    MobileBackgroundImage: string;

    /**
     * The border radius in pixels
     */
    BorderRadius: number;
    OverrideBorderRadius: boolean;

    /**
     * Canvas background color
     */
    CanvasBackgroundColor: string;
    CanvasBackgroundColorKey?: string;
    OverrideCanvasBackgroundColor: BoardThemeColorOverride;

    /**
     * Description of the dashboard
     */
    Desc: string;

    /**
     * All widget in the page following the grid space.
     */
    GridLayout: boolean;

    /**
     * icon in page list
     */
    Icon: string;

    /**
     * Defined if this page is shown
            in list with dashboards.
     */
    IsHidden: boolean;

    /**
     * Define the layout scale policy
     */
    LayoutPolicy: PageLayoutPolicy;

    /**
     * The margin of widgets
     */
    Margin: Margin;

    /**
     * Define tha maximum page width.
            If the window is have bigger width the page will add 
            empty space in left and right
     */
    MaxPageWidth: number;

    /**
     * The threshold in pixels that start translate the page to flat list
     */
    WidthListThreshold?: number;

    /**
     * Name of tha page
     */
    Name: string;

    /**
     * The number of vertical grids
            Default: 12
     */
    NumberOfVerticalGridSpaces: number;

    /**
     * The ordering in the specific level.
            0 is the first in list
     */
    Order: number;

    /**
     * We can have tree view for pages/dashboards
            the seperation of levels is with "/"
     */
    Path: string;

    /**
     * Polling settings
     */
    Polling: PollingSettings;

    /**
     * All the data of the page are going to be send with each port event.
     */
    SessionBasedPortData: boolean;

    /**
     * All widget are pull vertical up to fill any posiible space
     */
    VerticalCompact: boolean;

    /**
     * Default widget background color
     */
    WidgetBackgroundColor: string;
    WidgetBackgroundColorKey?: string;
    OverrideWidgetBackgroundColor: BoardThemeColorOverride;

    /**
     * No background in widgets
     */
    WidgetNoBackground: boolean;

    /**
     * ZDepth of widget paper component
     */
    ZDepth: number;
    OverrideZDepth: boolean;

    /**
     * Dashboard's page id
     */
    PageId: string;

    /**
     * Line that is drawn around the widget. 
     */
    Outline?: IOutline;

    /**
     * Define if the page is deleted
     */
    Deleted: boolean;

    /**
     * Padding for the page
     */
    Padding: number;
}
// --------------------------------------------------------------------------------------------
export const enum PageLayoutPolicy {

    /**
     * Keep aspect ratio
     */
    FillWidth = 0,

    /**
     * Fill tha page
     */
    FillHeight = 1,

    /**
     * keep aspect ration until the need of scollbar
     */
    AvoidScrollbar = 2
}
// --------------------------------------------------------------------------------------------
export interface Margin {
    H: number;
    W: number;
}
// --------------------------------------------------------------------------------------------
export const enum PollingScale {
    Sec = 1000,
    Min = 60000,
    Hours = 3600000
}
// --------------------------------------------------------------------------------------------
export interface PollingSettings {

    /**
     * Defined if the polling mechanism is enabled
     */
    Enabled: boolean;

    /**
     * Disable the period when the tab is not in focus
     */
    EnabledOnlyInFocus: boolean;

    /**
     * Define the polling period in seconds
     */
    Period: number;

    /**
     * Scale of the polling period sec/min/hours
     */
    PeriodScale: PollingScale;

    /**
     * Send poll immediately on tab refocus.
     */
    SendPollInRefocus: boolean;
}
// --------------------------------------------------------------------------------------------
export interface DashboardViewWidgets {
    Layout: { [key: string]: DashboardItemLayoutDesc };
    WidgetsDesc: { [key: string]: WidgetDescriptor };

    /**
     * Reporting pages. 
     * This is valid only for report type dashboard.
     */
    ReportPages?: { [key: string]: ReportPageDescriptor };
}
// --------------------------------------------------------------------------------------------
export interface DashboardItemLayoutDesc {
    H: number;
    IsDraggable?: boolean;
    IsResizable?: boolean;
    IsStatic?: boolean;
    Key: string;
    MaxH?: number;
    MaxW?: number;
    MinH?: number;
    MinW?: number;
    W: number;
    X: number;
    Y: number;
}
// --------------------------------------------------------------------------------------------
export interface WidgetDescriptor {
    Data: any;
    DefaultValues?: { [key: string]: any };
    DefiesGrid?: boolean;
    IsHidden?: boolean;
    Key: string;
    MinH: number;
    MinW: number;
    MobileIsHidden?: boolean;
    MobileOrder?: number;
    ShowExtraMenu?: boolean;
    StartWithLoading?: boolean;
    Title: string;
    TitleStyle?: WidgetTitleStyle;
    Type: string;
    WidgetNoBackground?: boolean;
    WidgetTitle?: string;
    WidgetBackgroundColor?: string;
    TrackUpdates?: ITrackUpdates;
    WidgetPadding?: IWidgetPadding;
    Outline?: IOutline;
    IconURI: string;
    ToolbarIcons?: ToolbarIconInfo[];
}
// --------------------------------------------------------------------------------------------
export interface IOutline {
    /**
     * The width of line that is drawn around the widget.
     */
    Thickness: number;
    /**
     * The color of line that is drawn around the widget.
     */
    Color: string;
}
// --------------------------------------------------------------------------------------------
export interface IWidgetPadding {
    Fixed: boolean;
    Common: boolean;
    All: number;
    Left: number;
    Right: number;
    Top: number;
    Bottom: number;
}
// --------------------------------------------------------------------------------------------
export interface ITrackUpdates {
    ShowLastUpdateTs: boolean;
    DateFormat: DateFormat;
    ViewFormat: ITsViewFormat;
    Timestamp?: string;
    IsExplicit?: boolean;
}
// --------------------------------------------------------------------------------------------
export enum ITsViewFormat {
    Tooltip,
    Text,
    Hover
}
// --------------------------------------------------------------------------------------------
export interface WidgetTitleStyle {
    Alignment: TitleAlign;
    BackgroundColor: string;
    FontColor: string;
    Icon: string;
    TitleHeight: number;
    FontAutoSize: boolean;
    FontSize: number;
    Tooltip?: string;
}
// --------------------------------------------------------------------------------------------
export enum TitleAlign {
    Left = "left",
    Center = "center",
    Right = "right"
}
// --------------------------------------------------------------------------------------------
export interface YodiBoardExt extends DashboardStructs.YodiBoard {
    /**
     * YodiBoard settings
            Filled at runtime
     */
    Settings: YodiBoardConfiguration;

    /**
     * Pure cyan nodekey 
     */
    CyanNodeKey: string;
}
export enum YodiBoardWSPolicy {
    Legacy = 'legacy',
    Partial = 'partial', // don't remove it even if it isn't used
    NoWS = 'no-ws'
}
// --------------------------------------------------------------------------------------------
export interface YodiBoardConfiguration {
    /**
     * Show board title instead of Board name
     */
    BoardAppTitle: string;

    /**
     * Automatic check if we have new version of board and refresh the local browser.
     */
    AutoUpdate: boolean;

    /**
     * Start with collapsed folders
     */
    CollapsedFolders: boolean;

    /**
     * Disable list support (show only the default page of tha board)
     */
    DefaultIsListEnabled: boolean;

    /**
     * Start with list open
     */
    DefaultIsListOpen: boolean;

    /**
     * Default page id
     */
    DefaultPageId: string;

    /**
     * Show title in page title
     */
    PageShowTitle: boolean;

    /**
     * Location service for GPS support
     */
    LocationService: boolean;

    /**
     * URL for logo in page list
     */
    Logo: string;

    /**
     * Maintenance information
     */
    Maintenance: DashboardMaintenance;

    /**
     * The threshold in pixels that start translate the page to flat list
     */
    WidthListThreshold: number;

    /**
     * Start the page in floating state.
     */
    DefaultIsListFloating: boolean;

    /**
     * Toolbar icons
     */
    ToolbarIcons: ToolbarIconInfo[];

    /**
     * Group
     */
    GroupName: string;

    /**
     * Hide top toolbar and move the icon elements to lower parts
     */
    HideTopToolbar: boolean;

    /**
     * Hide the ws connection status
     */
    HideWSConnection: boolean;

    /**
     * WS Policy
     */
    WSPolicy: YodiBoardWSPolicy;

    /**
     * hide the localization dropdown list
     */
    HideLocalizationDropdownList: boolean;

    /**
     * emits only an event on language change
     */
    HandleLocalizationFromGems: boolean;
}
// --------------------------------------------------------------------------------------------
/**
 * Information about dashboard's maintenance.
 */
export interface DashboardMaintenance {

    /**
     * Image to display in maintenance page (base64).
     */
    Image: string;

    /**
     * Is dashboard in maintenance phase?
     */
    IsEnabled: boolean;

    /**
     * The primary message for dashboard's maintenance.
     */
    PrimaryMsg: string;

    /**
     * The secondary message for dashboard's maintenance.
     */
    SecondaryMsg: string;
}
// --------------------------------------------------------------------------------------------
export interface ReportPageDescriptor {
    /**
     * Order of the page
     */
    Order: number;

    /**
     * Name of the page
     */
    Name: string;

    /**
     * Layout of the widgets in this reporting layout
     */
    Layout: { [key: string]: DashboardItemLayoutDesc };

    /**
     * Widget descriptions
     */
    WidgetsDesc: { [key: string]: WidgetDescriptor };

    /**
     * The page is showing an actual board page like a proxy.
     */
    PageProxyGUID: string;
}
// --------------------------------------------------------------------------------------------